import React, { useEffect, useRef, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CircularProgress } from '@mui/material'

import { checkAuthToken, selectTenant } from './store/slices/authSlice'
import { isClerkEnabled } from './config/clerkConfig'
import { consumirIngresoEnCurso, decidirRaiz } from './utils/postLogin'
import { isAuthEnabled as isSupabaseEnabled } from './config/supabaseClient'

// ¿Hay un proveedor de auth externo activo (Clerk o Supabase)?
const authEnabled = () => isClerkEnabled() || isSupabaseEnabled()

// lazy() con auto-recuperación: si el import dinámico de un chunk falla
// (típico tras un deploy — la pestaña tiene el index.html viejo y los chunks
// con hash antiguo ya no existen en el CDN), recarga la página UNA vez para
// obtener el HTML nuevo en vez de morir en pantalla blanca. El timestamp en
// sessionStorage evita un loop de recargas si el fallo persiste.
const lazyConRecarga = (factory) =>
  lazy(() =>
    factory().catch((err) => {
      const k = 'cd-chunk-reload-at'
      const ultima = Number(sessionStorage.getItem(k) || 0)
      if (Date.now() - ultima > 30_000) {
        sessionStorage.setItem(k, String(Date.now()))
        window.location.reload()
        return new Promise(() => {}) // la página ya se está recargando
      }
      throw err
    })
  )

// Pages — entry points cargadas eager (criticas para auth/landing)
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'

// Páginas de sign-in (lazy). Supabase tiene prioridad si está habilitado.
const SignInPageClerk = lazyConRecarga(() => import('./pages/SignInPageClerk'))
const SignInPageSupabase = lazyConRecarga(() => import('./pages/SignInPage'))
const SetPasswordPage = lazyConRecarga(() => import('./pages/SetPasswordPage'))
const RecuperarAccesoPage = lazyConRecarga(() => import('./pages/RecuperarAccesoPage'))
// SignUpPageClerk removido — el /sign-up directo creaba usuarios Clerk
// huérfanos (sin tenant) que después chocaban con 401 USER_NOT_LINKED.
// Ahora /sign-up redirige al flujo correcto de onboarding.

// Pages — lazy (code-split por route, descargadas on-demand)
const RegistroPage = lazyConRecarga(() => import('./pages/RegistroPage'))
const RegistroCompaniaPage = lazyConRecarga(() => import('./pages/RegistroCompaniaPage'))
const ConfirmarCorreoPage = lazyConRecarga(() => import('./pages/ConfirmarCorreoPage'))
const RegistroBomberoPage = lazyConRecarga(() => import('./pages/RegistroBomberoPage'))
const SolicitudesUnionPage = lazyConRecarga(() => import('./pages/SolicitudesUnionPage'))
const DashboardPage = lazyConRecarga(() => import('./pages/DashboardPage'))
const BomberosPage = lazyConRecarga(() => import('./pages/BomberosPage'))
const CitacionesPage = lazyConRecarga(() => import('./pages/CitacionesPage'))
const CircularesPage = lazyConRecarga(() => import('./pages/CircularesPage'))
const CuadroListasPage = lazyConRecarga(() => import('./pages/CuadroListasPage'))
const EmergenciasPage = lazyConRecarga(() => import('./pages/EmergenciasPage'))
const ClavesPage = lazyConRecarga(() => import('./pages/ClavesPage'))
const OficialesPage = lazyConRecarga(() => import('./pages/OficialesPage'))
const AdminPage = lazyConRecarga(() => import('./pages/AdminPage'))
const MaterialMenorPage = lazyConRecarga(() => import('./pages/MaterialMenorPage'))
const MaterialMayorPage = lazyConRecarga(() => import('./pages/MaterialMayorPage'))
const GuardiasPage = lazyConRecarga(() => import('./pages/GuardiasPage'))
const LicenciasPage = lazyConRecarga(() => import('./pages/LicenciasPage'))
const AdminLicenciasPage = lazyConRecarga(() => import('./pages/AdminLicenciasPage'))
const TiposBomberoPage = lazyConRecarga(() => import('./pages/TiposBomberoPage'))
const EspecialidadesPage = lazyConRecarga(() => import('./pages/EspecialidadesPage'))
const EspecialidadDetailPage = lazyConRecarga(() => import('./pages/EspecialidadDetailPage'))
const OperadoresPage = lazyConRecarga(() => import('./pages/OperadoresPage'))
const CompaniaServicioPage = lazyConRecarga(() => import('./pages/CompaniaServicioPage'))
const CuotasPage = lazyConRecarga(() => import('./pages/CuotasPage'))
const MisCuotasPage = lazyConRecarga(() => import('./pages/MisCuotasPage'))
const MisGuardiasPage = lazyConRecarga(() => import('./pages/MisGuardiasPage'))
const EstadisticasPage = lazyConRecarga(() => import('./pages/EstadisticasPage'))
const NotificacionesPage = lazyConRecarga(() => import('./pages/NotificacionesPage'))
const AdminNotificacionesPage = lazyConRecarga(() => import('./pages/AdminNotificacionesPage'))

// Portal público de autoatención (sin login) — /p/:slug/*
const PortalLayout = lazyConRecarga(() => import('./pages/portal/PortalLayout'))
const PortalInicio = lazyConRecarga(() => import('./pages/portal/PortalInicio'))
const PortalGuardia = lazyConRecarga(() => import('./pages/portal/PortalGuardia'))
const PortalCitaciones = lazyConRecarga(() => import('./pages/portal/PortalCitaciones'))
const PortalCirculares = lazyConRecarga(() => import('./pages/portal/PortalCirculares'))
const PortalEmergencias = lazyConRecarga(() => import('./pages/portal/PortalEmergencias'))
const PortalCuotas = lazyConRecarga(() => import('./pages/portal/PortalCuotas'))
const PortalCuadro = lazyConRecarga(() => import('./pages/portal/PortalCuadro'))
const PortalClaves = lazyConRecarga(() => import('./pages/portal/PortalClaves'))
const PortalSolicitarLicencia = lazyConRecarga(() => import('./pages/portal/PortalSolicitarLicencia'))
const PortalRegistrarParte = lazyConRecarga(() => import('./pages/portal/PortalRegistrarParte'))
const PortalSolicitarReemplazo = lazyConRecarga(() => import('./pages/portal/PortalSolicitarReemplazo'))

// Pantalla / Diario Mural nativo
const PantallaDisplay = lazyConRecarga(() => import('./pages/PantallaDisplay'))
const PantallaEditorPage = lazyConRecarga(() => import('./pages/PantallaEditorPage'))

// Components
import ProtectedRoute from './components/ProtectedRoute'
import TenantRoute from './components/TenantRoute'
import Layout from './components/Layout'
import DemoSessionBoot from './components/DemoSessionBoot'
import RequireCapability from './components/RequireCapability'

// Fallback de Suspense mientras se descarga el chunk
const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', width: '100%' }}>
    <CircularProgress />
  </Box>
)

/**
 * Ruta raíz: landing pública, salvo que esta visita sea el aterrizaje de un login.
 *
 * Tener sesión abierta NO manda al dashboard — un usuario logueado que escribe la dirección
 * principal quiere ver la landing (antes rebotaba al dashboard y no había forma de llegar).
 * El redirect queda sólo para los flujos que terminan en "/" y esperan continuar al panel
 * (OAuth de Supabase, fallback de Clerk, establecer-password), que dejan la marca de
 * `utils/postLogin`. Desde la landing se vuelve al panel con el botón "Ir a mi panel".
 */
const AuthRedirect = () => {
  const { isAuthenticated, loading, bootstrapping } = useSelector((state) => state.auth)
  const tenant = useSelector(selectTenant)
  // Se consume UNA vez por montaje (no en cada render): la marca se borra al leerla, y este
  // componente re-renderiza varias veces mientras la sesión se hidrata.
  const volviendoDeLogin = useRef(null)
  if (volviendoDeLogin.current === null) volviendoDeLogin.current = consumirIngresoEnCurso()

  const destino = decidirRaiz({
    volviendoDeLogin: volviendoDeLogin.current,
    isAuthenticated,
    slug: tenant?.slug,
    cargando: bootstrapping || loading,
  })
  if (destino === 'dashboard') return <Navigate to={`/${tenant.slug}/dashboard`} replace />
  if (destino === 'espera') return <RouteFallback />
  return <LandingPage />
}

function App() {
  const dispatch = useDispatch()

  // Verificar token al cargar la aplicación
  useEffect(() => {
    dispatch(checkAuthToken())
  }, [dispatch])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'var(--cd-full-vh, 100vh)' }}>
      {/* Sesión anónima del tenant demo cuando la ruta es /demo/* */}
      <DemoSessionBoot />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Rutas públicas */}
          <Route path="/" element={<AuthRedirect />} />
          <Route
            path="/login"
            element={authEnabled() ? <Navigate to="/sign-in" replace /> : <LoginPage />}
          />
          <Route
            path="/:slug/login"
            element={authEnabled() ? <Navigate to="/sign-in" replace /> : <LoginPage />}
          />
          <Route
            path="/registro"
            element={authEnabled() ? <Navigate to="/registro/compania" replace /> : <RegistroPage />}
          />
          <Route path="/registro/compania" element={<RegistroCompaniaPage />} />
          <Route path="/confirmar-correo" element={<ConfirmarCorreoPage />} />
          <Route path="/registro/bombero" element={<RegistroBomberoPage />} />

          {/* Sign-in del proveedor activo. Supabase tiene prioridad; si no, Clerk;
              si ninguno está habilitado, /sign-in redirige a /login (evita montar
              <SignIn> sin provider → pantalla blanca). */}
          <Route
            path="/sign-in/*"
            element={
              isSupabaseEnabled() ? <SignInPageSupabase />
              : isClerkEnabled() ? <SignInPageClerk />
              : <Navigate to="/login" replace />
            }
          />
          <Route path="/sign-up/*" element={<Navigate to="/registro/compania" replace />} />
          <Route
            path="/recuperar-acceso"
            element={isSupabaseEnabled() ? <RecuperarAccesoPage /> : <Navigate to="/sign-in" replace />}
          />
          {/* Destino de links de invitación/recuperación de Supabase */}
          <Route path="/establecer-password" element={<SetPasswordPage />} />

          {/* Portal público de autoatención (sin login), company-first: /:slug/portal/* */}
          <Route path="/:slug/portal" element={<PortalLayout />}>
            <Route index element={<PortalInicio />} />
            <Route path="guardia" element={<PortalGuardia />} />
            <Route path="citaciones" element={<PortalCitaciones />} />
            <Route path="citaciones/:id" element={<PortalCitaciones />} />
            <Route path="circulares" element={<PortalCirculares />} />
            <Route path="circulares/:id" element={<PortalCirculares />} />
            <Route path="emergencias" element={<PortalEmergencias />} />
            <Route path="cuotas" element={<PortalCuotas />} />
            <Route path="cuadro" element={<PortalCuadro />} />
            <Route path="claves" element={<PortalClaves />} />
            <Route path="licencias/solicitar" element={<PortalSolicitarLicencia />} />
            <Route path="emergencias/registrar" element={<PortalRegistrarParte />} />
            <Route path="guardia/reemplazo" element={<PortalSolicitarReemplazo />} />
          </Route>

          {/* Kiosko público de la TV (sin login; gate por contraseña), company-first: /:slug/pantalla */}
          <Route path="/:slug/pantalla" element={<PantallaDisplay />} />

          {/* Rutas de tenant autenticadas bajo /:slug/* */}
          <Route path="/:slug" element={<TenantRoute />}>
            <Route element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              {/* Dashboard principal */}
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* Módulos principales */}
              <Route path="bomberos" element={<BomberosPage />} />
              <Route path="tipos-bombero" element={<RequireCapability modulo="tipos-bombero"><TiposBomberoPage /></RequireCapability>} />
              <Route path="especialidades" element={<EspecialidadesPage />} />
              <Route path="operadores" element={<RequireCapability modulo="operadores"><OperadoresPage /></RequireCapability>} />
              <Route path="compania-servicio" element={<RequireCapability modulo="compania-servicio"><CompaniaServicioPage /></RequireCapability>} />
              <Route path="especialidades/:id" element={<EspecialidadDetailPage />} />
              <Route path="citaciones" element={<CitacionesPage />} />
              <Route path="circulares" element={<CircularesPage />} />
              <Route path="circulares/:id" element={<CircularesPage />} />
              <Route path="cuadro-listas" element={<CuadroListasPage />} />
              <Route path="emergencias" element={<EmergenciasPage />} />
              <Route path="claves" element={<ClavesPage />} />
              <Route path="oficiales" element={<OficialesPage />} />
              <Route path="material" element={<MaterialMenorPage />} />
              <Route path="material-mayor" element={<MaterialMayorPage />} />
              {/* Guardia Nocturna visible para todos: gestión completa para admin
                  y cargos con permiso; solo-lectura para el bombero raso (las
                  acciones de escritura se ocultan dentro de la página). */}
              <Route path="guardias" element={<GuardiasPage />} />
              <Route path="estadisticas" element={<RequireCapability modulo="estadisticas"><EstadisticasPage /></RequireCapability>} />
              <Route path="licencias" element={<LicenciasPage />} />
              <Route path="cuotas" element={<RequireCapability modulo="cuotas" redirectTo="mis-cuotas"><CuotasPage /></RequireCapability>} />
              <Route path="mis-cuotas" element={<MisCuotasPage />} />
              <Route path="mis-guardias" element={<MisGuardiasPage />} />
              <Route path="admin/solicitudes" element={<SolicitudesUnionPage />} />

              {/* Notificaciones */}
              <Route path="notificaciones" element={<NotificacionesPage />} />
              <Route path="admin/notificaciones" element={<RequireCapability modulo="notificaciones.escritura"><AdminNotificacionesPage /></RequireCapability>} />

              {/* Panel administrativo */}
              <Route path="admin" element={<RequireCapability adminOnly><AdminPage /></RequireCapability>} />
              <Route path="admin/licencias" element={<RequireCapability modulo="licencias.escritura"><AdminLicenciasPage /></RequireCapability>} />
              <Route path="pantalla-config" element={<RequireCapability adminOnly><PantallaEditorPage /></RequireCapability>} />
            </Route>
          </Route>

          {/* Ruta por defecto */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Box>
  )
}

export default App
